import React, { useState } from "react";
// @material-ui/core components
import FormLabel from "@material-ui/core/FormLabel";
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/icons
import Contacts from "@material-ui/icons/Contacts";

// core components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";

import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";

import Select from "components/Select/Select";
import { ethers } from "ethers";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { bridgeService } from "services/bridgeService";
import BridgeTokenABI from "../../contracts/BridgeToken.json";

const useStyles = makeStyles(styles);

export const bridgeContract = {
  // mainnet
  25: "0x01EEffCf58D26a63c0C633f120b0A3310EA6011C",
  137: "0xfacfa2c1A843c8564d0804d70c4203710229dCF9",
  56: "0x44DCb513C58daF4b6D6B611E3136FDd275bc2052",
  8453: "0xaff2a1a1a700d102D9808e087873fA39BfE74ae6",
  81457: "0x97C8202f4BB4A98D13aBe20837885ADe16feB062",
  43114: "0x8d3cf46294E58511Ce1cCE750ED36c07b0707c0F",
  42161: "0x8d3cf46294E58511Ce1cCE750ED36c07b0707c0F",
  10: "0x8d3cf46294E58511Ce1cCE750ED36c07b0707c0F",
  // testnet
  80002: "0xc32aFe5046dCDacd388Ce737E175dDaD67Ebf663",
  84532: "0x1CE225992891967827AF58F59C05F6B4D9Bb90b7",
};

const testnet = [
  { value: 97, label: "BSC Testnet" },
  { value: 84532, label: "Base Testnet" },
  { value: 80002, label: "Amoy" },
];

const mainnet = [
  { value: 56, label: "BNB Smart Chain" },
  { value: 8453, label: "Base" },
  { value: 137, label: "Polygon" },
  { value: 25, label: "Cronos" },
  { value: 81457, label: "Blast" },
  { value: 43114, label: "Avalanche C-Chain" },
  { value: 42161, label: "Arbitrum One" },
  { value: 10, label: "OP Mainnet" },
];

export const optionChain = process.env.NODE_ENV === "development" ? testnet : mainnet;

export default function BridgeCreate() {
  const {
    register: bridge,
    handleSubmit,
    setValue,
    control,
    watch,
  } = useForm({
    defaultValues: {
      token: "",
      tokenName: "",
      currentChainId: 25,
      targetChainId: 137,
      targetToken: "",
      targetTokenName: "",
    },
  });
  const { id } = useParams();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        await window.ethereum.request({ method: "eth_requestAccounts" });
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        const address = await signer.getAddress();
        const network = provider.getNetwork();
        const chainId = (await network).chainId;

        return { signer, address, chainId, provider };
      } catch (error) {
        console.error("Failed to connect wallet:", error.message);
        toast.error("ailed to connect walle!");
      }
    } else {
      console.error("MetaMask is not installed!");
      toast.error("MetaMask is not installed!");
    }
  };

  const formValidate = (payload) => {
    if (!payload.token) {
      toast.error("Please enter token address");
      return true;
    }
    if (!payload.tokenName) {
      toast.error("Please enter token name");
      return true;
    }
    if (!payload.currentChainId) {
      toast.error("Please enter current chainId");
      return true;
    }
    if (!payload.targetChainId) {
      toast.error("Please enter target chainId");
      return true;
    }
    if (!payload.targetToken) {
      toast.error("Please enter target token address");
      return true;
    }
    if (!payload.targetTokenName) {
      toast.error("Please enter target token name");
      return true;
    }

    return false;
  };

  async function create(payload) {
    try {
      if (formValidate(payload)) return;
      setIsLoading(true);
      const { signer, chainId, provider } = await connectWallet();

      if (payload.currentChainId !== chainId) {
        await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: `0x${parseInt(payload.currentChainId, 10).toString(16)}` }],
        });
      }

      const bridgeTokenContract = new ethers.Contract(
        bridgeContract[payload.currentChainId],
        BridgeTokenABI,
        signer,
      );
      const tx = await bridgeTokenContract.approveToken(
        payload.token,
        +payload.targetChainId,
        payload.targetToken,
      );
      await tx.wait();

      const receipt = await provider.getTransactionReceipt(tx.hash);

      if (receipt) {
        if (receipt.status === 1) {
          await bridgeService.create(payload);
          toast.success("Create success!");
          console.log("Transaction succeeded");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.message[0]);
      toast.error(e?.message);
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  async function update(payload) {
    try {
      try {
        if (!id) return;
        if (formValidate(payload)) return;
        setIsLoading(true);
        const { signer, chainId, provider } = await connectWallet();

        if (payload.currentChainId !== chainId) {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${parseInt(payload.currentChainId, 10).toString(16)}` }],
          });
        }

        const bridgeTokenContract = new ethers.Contract(
          bridgeContract[payload.currentChainId],
          BridgeTokenABI,
          signer,
        );
        const tx = await bridgeTokenContract.approveToken(
          payload.token,
          +payload.targetChainId,
          payload.targetToken,
        );
        await tx.wait();

        const receipt = await provider.getTransactionReceipt(tx.hash);

        if (receipt) {
          if (receipt.status === 1) {
            await bridgeService.update(id, payload);
            toast.success("Update success!");
            console.log("Transaction succeeded");
          }
        }
      } catch (e) {
        toast.error(e?.response?.data?.message[0]);
        toast.error(e?.message);
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message[0] || "Cannot update");
      console.log(e);
    }
  }

  async function onSubmit(form) {
    if (isLoading) return;
    if (id) {
      update(form);
    } else {
      create(form);
    }
  }

  async function fetchBridge() {
    try {
      const { data } = await bridgeService.view(id);
      const fields = [
        "token",
        "tokenName",
        "targetChainId",
        "currentChainId",
        "targetToken",
        "targetTokenName",
      ];
      fields.forEach((field) => setValue(field, data[field]));
    } catch (e) {
      console.log(e);
    }
  }

  React.useEffect(() => {
    if (id) {
      fetchBridge();
    }
  }, [id]);

  const classes = useStyles();
  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Contacts />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>{!id ? "Setup token" : "Edit token"}</h4>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <GridContainer>
                <GridItem xs={12} sm={12} md={8}>
                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>Current ChainId</FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <Controller
                        control={control}
                        name="currentChainId"
                        render={({ field: { value, onChange } }) => (
                          <Select
                            name="currentChainId"
                            label=" "
                            onChange={onChange}
                            value={value}
                            options={optionChain.filter(
                              (chain) => chain.value !== watch("targetChainId"),
                            )}
                          />
                        )}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>Token Address</FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "text",
                          ...bridge("token"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>Token Name</FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "text",
                          ...bridge("tokenName"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>Target ChainId</FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <Controller
                        control={control}
                        name="targetChainId"
                        render={({ field: { value, onChange } }) => (
                          <Select
                            name="targetChainId"
                            label=" "
                            onChange={onChange}
                            value={value}
                            options={optionChain.filter(
                              (chain) => chain.value !== watch("currentChainId"),
                            )}
                          />
                        )}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>
                        Target Token Address
                      </FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "text",
                          ...bridge("targetToken"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer>
                    <GridItem xs={12} sm={12} md={3}>
                      <FormLabel className={classes.labelHorizontal}>Target Token Name</FormLabel>
                    </GridItem>
                    <GridItem xs={12} sm={12} md={7}>
                      <CustomInput
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: "text",
                          ...bridge("targetTokenName"),
                        }}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer justify="flex-end">
                    <GridItem xs={12} sm={12} md={9}>
                      <Button onClick={() => history.goBack()}>Cancel</Button>
                      <Button color="rose" type="submit">
                        {isLoading ? "Loading..." : "Create"}
                      </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </form>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
